import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { Navigate, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BiArrowBack } from 'react-icons/bi';
import { parivartanAPI } from '../../../services/parivartanAPI';
import WorkspaceDataTable from '../../../components/workspace/WorkspaceDataTable';
import WorkspacePageHeader from '../../../components/workspace/WorkspacePageHeader';
import { useAdminRecordDelete } from '../../../components/workspace/useAdminRecordDelete';
import { revisionColumn, useMasterRevision } from '../../../components/workspace/useMasterRevision';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { canManageMasters } from '../../../utils/permissions';
import './CustomerMaster.scss';

const ServiceListPage = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useDocumentTitle('Services');

  const load = useCallback(async () => {
    setError('');
    try {
      const response = await parivartanAPI.listServices();
      setRows(response.data?.data?.services || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load services');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const admin = useAdminRecordDelete({
    entity: 'service',
    noun: 'service',
    plural: 'services',
    reload: load,
    label: (row) => row.name
  });
  const revision = useMasterRevision({ entity: 'service' });

  const columns = useMemo(
    () => [
      { key: 'serviceNo', label: 'Service no.', width: '130px', mobileTitle: true },
      { key: 'code', label: 'Code', width: '90px' },
      {
        key: 'name',
        label: 'Name',
        render: (value, row) => (
          <div className="cust-name">
            <span className="cust-name__title">{value}</span>
            {row.deliverable ? <span className="cust-name__meta">{row.deliverable}</span> : null}
          </div>
        )
      },
      { key: 'category', label: 'Category', width: '200px' },
      { key: 'billingBasis', label: 'Billing', width: '180px' },
      revisionColumn,
      {
        key: 'status',
        label: 'Status',
        width: '120px',
        render: (value) => (
          <span className={`cust-status cust-status--${value === 'active' ? 'active' : 'inactive'}`}>
            {value === 'active' ? 'Active' : 'Inactive'}
          </span>
        )
      }
    ],
    []
  );

  if (!canManageMasters(user?.permissions, user?.roles)) {
    return <Navigate to="/apps/tender" replace />;
  }

  if (loading) return <Spinner animation="border" size="sm" />;

  return (
    <div className="cust-page">
      <button type="button" className="cust-page__back" onClick={() => navigate('/apps/tender/masters')}>
        <BiArrowBack />
        Masters
      </button>
      <WorkspacePageHeader
        title="Services"
        help="services"
        subtitle="The services Dhruv offers. Choose one of these when a tender is captured."
        actions={(
          <>
            <span className="cust-page__count">Total records {rows.length}</span>
            <Button size="sm" variant="primary" onClick={() => navigate('/apps/tender/masters/services/new')}>
              New
            </Button>
          </>
        )}
      />
      {error ? <Alert variant="danger">{error}</Alert> : null}
      {admin.notice}
      <WorkspaceDataTable
        downloadPermission="masters.download"
        title="Services"
        data={rows}
        columns={columns}
        searchPlaceholder="Search number, code, name, category…"
        pageSize={12}
        onView={(row) => navigate(`/apps/tender/masters/services/${row._id}`)}
        onEdit={(row) => navigate(`/apps/tender/masters/services/${row._id}/edit`)}
        {...admin.tableProps}
        rowActions={revision.wrapActions(admin.wrapActions(() => [
          { type: 'view', label: 'View' },
          { type: 'edit', label: 'Edit' },
          { type: 'delete', label: 'Delete', variant: 'danger' }
        ]))}
      />
      {admin.modal}
      {revision.modals}
    </div>
  );
};

export default ServiceListPage;
